import { useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

export const TOUR_VIDEO = {
  src: "/video/duck-the-bugs-tour-v2.mp4",
  poster: "/video/duck-the-bugs-tour-poster.jpg",
  duration: "0:26",
};

// Homepage site tour (in the hero). Browsers only autoplay video with the sound
// off, so it starts muted and looped while it is on screen, pauses when
// scrolled away, and offers a "Tap for sound" button. Pressing play yourself
// starts it with sound. Visitors who prefer reduced motion get the poster and
// a play button instead of autoplay.
export default function VideoTour({ className = "" }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const userPaused = useRef(false);
  const autoPausing = useRef(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    // React does not reliably write the `muted` attribute, and mobile browsers
    // only autoplay muted video, so set the property directly.
    v.muted = true;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // navigator.webdriver: skip autoplay during the build's prerender pass.
    if (reduce || navigator.webdriver || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.35) {
          if (!userPaused.current && v.paused) v.play().catch(() => {});
        } else if (!v.paused) {
          autoPausing.current = true;
          v.pause();
        }
      },
      { threshold: [0, 0.35] }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  // Pressing play is a deliberate choice, so play with sound.
  const playWithSound = () => {
    const v = ref.current;
    if (!v) return;
    userPaused.current = false;
    v.muted = false;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
  };

  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.paused) {
      userPaused.current = false;
      v.play().catch(() => {});
    }
  };

  return (
    <div
      className={`relative aspect-video rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden bg-forest-950 ring-1 ring-cream/20 shadow-2xl shadow-black/40 ${className}`}
    >
      <video
        ref={ref}
        className="absolute inset-0 w-full h-full object-cover"
        poster={TOUR_VIDEO.poster}
        preload="none"
        playsInline
        loop
        controls={started}
        aria-label="A 26-second tour of DuckTheBugs: pick the pest, get straight answers, decide whether it's a DIY fix, and get pointed to a local pro when you need one."
        onPlay={() => {
          userPaused.current = false;
          setStarted(true);
        }}
        onPause={() => {
          if (autoPausing.current) autoPausing.current = false;
          else userPaused.current = true;
        }}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        data-testid="home-tour-video"
      >
        <source src={TOUR_VIDEO.src} type="video/mp4" />
      </video>

      {!started && (
        <button
          type="button"
          onClick={playWithSound}
          className="group absolute inset-0 flex items-end justify-end p-3 sm:p-5 bg-transparent"
          aria-label={`Play the ${TOUR_VIDEO.duration} tour`}
          data-testid="home-tour-play"
        >
          <span className="inline-flex items-center gap-2.5 rounded-full bg-forest-950 text-cream shadow-xl shadow-forest-950/25 p-1.5 sm:pr-5 transition-transform group-hover:scale-[1.03] group-active:scale-95">
            <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-duckgreen flex items-center justify-center">
              <Play className="w-4 h-4 sm:w-5 sm:h-5 text-forest-950 fill-forest-950 ml-0.5" />
            </span>
            <span className="hidden sm:inline font-semibold text-sm">
              Watch the tour <span className="text-cream/60 font-normal">· {TOUR_VIDEO.duration}</span>
            </span>
          </span>
        </button>
      )}

      {started && (
        <button
          type="button"
          onClick={toggleSound}
          className={`absolute top-2.5 right-2.5 sm:top-4 sm:right-4 inline-flex items-center gap-2 rounded-full bg-forest-950/85 backdrop-blur text-cream font-semibold text-sm shadow-lg shadow-black/25 min-h-[44px] transition-all active:scale-95 ${
            muted ? "pl-3.5 pr-4" : "w-11 justify-center"
          }`}
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
          data-testid="home-tour-sound"
        >
          {muted ? (
            <>
              <VolumeX className="w-4 h-4 text-duckgreen" />
              Tap for sound
            </>
          ) : (
            <Volume2 className="w-4 h-4 text-duckgreen" />
          )}
        </button>
      )}
    </div>
  );
}
